'use client';

import { FC } from 'react';
import {
  TAssetForTransferArray,
  TransfersSectionVariants,
} from '../../../types';
import { TransferEntityPickerEntry } from '../TransferEntityPickerEntry/TransferEntityPickerEntry';
import { TCryptoTransferTranslations } from '../../../utils/getCryptoTransferTranslations';
import { TTransfersNextStepStateWithActions } from '../../../utils/useTransfersNextStepState.client';
import { css } from '@bts-web/utils-style-engine';
import { TwoColumnHeaders } from '../../../../common/components/TwoColumnHeaders/TwoColumnHeaders';
import {
  intlAssetValueFormatting,
  PrecisionFloat,
} from '@bts-web/utils-formatting';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { HTMLRenderer } from '@bts-web/core-features/common';

export interface INetworkPickerListProps {
  onNetworkSelected: (
    network: NonNullable<
      NonNullable<
        NonNullable<TAssetForTransferArray[0]['networkInformation']>['networks']
      >[0]
    >,
  ) => void;
  networkList: NonNullable<
    NonNullable<
      TAssetForTransferArray[number]['networkInformation']
    >['networks']
  >;
  variant: TransfersSectionVariants;
  translations: TCryptoTransferTranslations;
  state: TTransfersNextStepStateWithActions;
  locale: string;
}

const NetworkPickerList: FC<INetworkPickerListProps> = ({
  onNetworkSelected,
  networkList,
  variant,
  state,
  locale,
}) => {
  const { selectedNetworkData, selectedAssetData, addressInput } = state;

  const isWithdrawal = variant === TransfersSectionVariants.Send;

  const { t } = useClientTranslation();

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 'medium',
      })}
    >
      {isWithdrawal && (
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'small',
          })}
        >
          <h2
            className={css({
              color: 'neutrals.text_primary',
              fontSize: 'headline.large_semi_bold',
              fontWeight: 'headline.large_semi_bold',
              letterSpacing: 'headline.large_semi_bold',
              lineHeight: 'headline.large_semi_bold',
            })}
          >
            {t('crypto_withdrawal_choose_network_heading', {
              assetSymbol: selectedAssetData?.symbol,
            })}
          </h2>

          <p
            className={css({
              color: 'neutrals.text_primary',
              fontSize: 'body.medium',
              fontWeight: 'body.medium',
              letterSpacing: 'body.medium',
              lineHeight: 'body.medium',
              '& > [data-element="markdown-style"]': {
                fontSize: 'label.medium_bold',
                fontWeight: 'label.medium_bold',
                lineHeight: 'label.medium_bold',
                letterSpacing: 'label.medium_bold',
              },
            })}
          >
            {addressInput?.address &&
              t('crypto_withdrawal_associate_network_to_address', {
                walletAddress: addressInput?.address,
              })}

            {addressInput?.ownerLegalName && (
              <HTMLRenderer>
                {t('crypto_withdrawal_associate_network_to_legal_name', {
                  ownerLegalName: addressInput?.ownerLegalName,
                })}
              </HTMLRenderer>
            )}

            {!addressInput?.address &&
              !addressInput?.ownerLegalName &&
              t('crypto_withdrawal_associate_network_to_new_wallet')}
          </p>
        </div>
      )}

      <div>
        {isWithdrawal && (
          <TwoColumnHeaders
            firstColumnHeader="Network"
            secondColumnHeader="Fee"
            withBorderBottom
          />
        )}

        <ul aria-label="network selection list">
          {networkList.map((network) => {
            if (!network) return null;

            let fee = undefined;

            if (isWithdrawal) {
              const feePrecisionFloat =
                network.minWithdrawalFee as PrecisionFloat;

              fee =
                !feePrecisionFloat || Number(feePrecisionFloat.value) === 0
                  ? '0'
                  : `${intlAssetValueFormatting(feePrecisionFloat.value, {
                      locale,
                      maximumFractionDigits: feePrecisionFloat.precision,
                    })} ${selectedAssetData?.symbol}`;
            }

            return (
              <TransferEntityPickerEntry
                withinList
                title={network.name ?? ''}
                type="entryWithActiveState"
                active={selectedNetworkData?.id === network.id}
                imageUrl={network.logoUrl ?? undefined}
                onPick={() => onNetworkSelected(network)}
                key={network.id}
                endInfoMain={fee}
              />
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export { NetworkPickerList };
